// Infinite scroll for product grid using GSAP
class InfiniteProductGrid {
  constructor() {
    this.container = document.querySelector('[data-collection-handle]');
    if (!this.container) return;

    this.collectionHandle = this.container.dataset.collectionHandle;
    this.currentPage = parseInt(this.container.dataset.currentPage);
    this.productsPerPage = parseInt(this.container.dataset.productsPerPage);
    this.totalPages = parseInt(this.container.dataset.totalPages);
    this.sectionId = this.container.dataset.sectionId;
    this.loading = false;
    this.loadingIndicator = document.getElementById('loadingIndicator');
    this.endIndicator = document.getElementById('endOfProducts');

    this.init();
  }

  init() {
    // Create GSAP ScrollTrigger for infinite scroll
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.create({
        trigger: this.container,
        start: 'bottom bottom+=100px',
        // markers: true,
        onEnter: () => this.loadMoreProducts(),
        onEnterBack: () => this.loadMoreProducts(),
      });
    } else {
      // Fallback to regular scroll event if GSAP ScrollTrigger is not available
      this.initFallbackScroll();
    }
  }

  initFallbackScroll() {
    let ticking = false;

    const checkScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Trigger when user is 100px from bottom
      if (scrollTop + windowHeight >= documentHeight - 100) {
        this.loadMoreProducts();
      }
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(checkScroll);
        ticking = true;
      }
    });
  }

  async loadMoreProducts() {
    if (this.loading || this.currentPage >= this.totalPages) {
      if (this.currentPage >= this.totalPages) {
        this.showEndIndicator();
      }
      return;
    }

    this.loading = true;
    this.showLoadingIndicator();

    try {
      const nextPage = this.currentPage + 1;
      const url = `/collections/${this.collectionHandle}?page=${nextPage}&view=ajax`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Look for products in the response
      const newProducts = doc.querySelectorAll('.product-item');

      if (newProducts.length > 0) {
        this.animateNewProducts(newProducts);
        this.currentPage = nextPage;
        this.container.dataset.currentPage = nextPage;
        if (this.currentPage >= this.totalPages) {
          this.showEndIndicator();
        }
      } else {
        this.showEndIndicator();
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      this.showEndIndicator();
    } finally {
      this.loading = false;
      this.hideLoadingIndicator();
    }
  }

  // Alternative method using direct product data
  async loadMoreProductsAlternative() {
    try {
      const nextPage = this.currentPage + 1;
      const url = `/collections/${this.collectionHandle}/products.json?page=${nextPage}&limit=${this.productsPerPage}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.renderProductsFromData(data.products);
        this.currentPage = nextPage;
        this.container.dataset.currentPage = nextPage;
      }
    } catch (error) {
      console.error('Alternative loading method failed:', error);
    }
  }

  renderProductsFromData(products) {
    const fragment = document.createDocumentFragment();

    products.forEach((product, index) => {
      const productElement = this.createProductElement(product);
      gsap.set(productElement, {
        opacity: 0,
        y: 50,
        scale: 0.8,
      });
      fragment.appendChild(productElement);
    });

    this.container.appendChild(fragment);

    const newlyAddedProducts = this.container.querySelectorAll(
      '.product-item:nth-last-child(-n+' + products.length + ')',
    );

    gsap.to(newlyAddedProducts, {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.6,
      ease: 'power2.out',
      stagger: 0.1,
    });
  }

  createProductElement(product) {
    const productDiv = document.createElement('div');
    productDiv.className = 'relative group product-item';
    productDiv.setAttribute('data-product-id', product.id);

    const imageUrl = product.images.length > 0 ? product.images[0].src : '';
    const secondImageUrl =
      product.images.length > 1 ? product.images[1].src : '';
    const altText =
      product.images.length > 0 ? product.images[0].alt : product.title;

    productDiv.innerHTML = `
      <a href="/products/${product.handle}" class="relative block h-[70vh] lg:h-[55vh]">
        <img
          src="${imageUrl}?width=1000"
          alt="${altText}"
          class="object-contain w-full h-[70vh] z-0 lg:h-[53vh]"
          width="100%"
          height="auto">
        ${
          secondImageUrl
            ? `
        <img
          src="${secondImageUrl}?width=700"
          alt="${altText}"
          class="absolute top-0 left-0 object-contain z-0 w-full h-[70vh] lg:h-[53vh] opacity-0
          group-hover:opacity-100 transition-opacity duration-300"
          width="700"
          height="700">
        `
            : ''
        }
      </a>
      <div class="absolute bottom-0 inset-x-0 bg-white m-[1rem] flex h-[30px] select-none cursor-pointer
      font-monospace text-[.7rem] text-black tracking-tight justify-between items-center px-[.5rem] py-[.25rem]
      border-[1px] border-white transition-all duration-300 ease-in-out group-hover:border-black">
        <span class="mt-[1px]">
          ${product.title}
        </span>
        <div class="mt-[1px] space-x-2">
          <span>
            ${this.formatMoney(product.variants[0].price)}
          </span>
        </div>
      </div>
    `;

    return productDiv;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  }

  animateNewProducts(newProducts) {
    const fragment = document.createDocumentFragment();

    newProducts.forEach((product, index) => {
      const clonedProduct = product.cloneNode(true);
      // Set initial state for animation
      gsap.set(clonedProduct, {
        opacity: 0,
      });
      fragment.appendChild(clonedProduct);
    });

    // Append all new products at once
    this.container.appendChild(fragment);

    // Animate them in with stagger
    const newlyAddedProducts = this.container.querySelectorAll(
      '.product-item:nth-last-child(-n+' + newProducts.length + ')',
    );

    gsap.to(newlyAddedProducts, {
      opacity: 1,
      duration: 1,
      ease: 'power2.out',
      stagger: 0.15,
    });
  }

  showLoadingIndicator() {
    this.loadingIndicator.classList.remove('hidden');
    this.loadingIndicator.classList.add('block');

    // Animate in the loading indicator
    gsap.fromTo(
      this.loadingIndicator,
      { opacity: 0 },
      { opacity: 1, duration: 0.3 },
    );
  }

  hideLoadingIndicator() {
    gsap.to(this.loadingIndicator, {
      opacity: 0,
      y: -20,
      duration: 0.3,
      onComplete: () => {
        this.loadingIndicator.classList.add('hidden');
        this.loadingIndicator.classList.remove('block');
      },
    });
  }

  showEndIndicator() {
    this.endIndicator.classList.remove('hidden');
    this.endIndicator.classList.add('block');

    gsap.fromTo(
      this.endIndicator,
      { opacity: 0 },
      { opacity: 1, duration: 0.5 },
    );
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new InfiniteProductGrid();
});

// Re-initialize on section reloads (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('sieg-product-grid')) {
    new InfiniteProductGrid();
  }
});
